import React, { useState, useEffect, useMemo, useCallback } from "react";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import Swal from 'sweetalert2';
import {
  FiSearch, FiBox, FiTag, FiChevronsUp, FiChevronsDown,
  FiChevronLeft, FiChevronRight, FiEdit3, FiMinus,
  FiPlusCircle, FiAlertTriangle, FiPackage, FiDollarSign,
  FiLayers, FiX, FiZap, FiCheck,
} from "react-icons/fi";
import axios from "axios";
import { baseurl, imgurl } from "../../baseurl";
import { LockIcon } from "lucide-react";

/* ─────────────────────────────────────────────
   CSS
───────────────────────────────────────────── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=DM+Sans:wght@400;500;600&display=swap');

:root {
  --sa-teal:      #0a5556;
  --sa-teal-mid:  #0c7172;
  --sa-teal-lite: #14b8a6;
  --sa-gold:      #FFD166;
  --sa-bg:        #eef6f6;
  --sa-white:     #ffffff;
  --sa-border:    rgba(10,85,86,0.10);
  --sa-text:      #0f2a2a;
  --sa-muted:     #5a8080;
  --sa-danger:    #ef4444;
  --sa-success:   #10b981;
  --sa-warning:   #f59e0b;
}

@keyframes sa__fadeUp  { from{opacity:0;transform:translateY(18px)} to{opacity:1;transform:translateY(0)} }
@keyframes sa__shimmer { 0%{background-position:-600px 0} 100%{background-position:600px 0} }
@keyframes sa__spin    { to{transform:rotate(360deg)} }
@keyframes sa__modalIn { from{opacity:0;transform:scale(0.93) translateY(14px)} to{opacity:1;transform:scale(1) translateY(0)} }
@keyframes sa__rowIn   { from{opacity:0;transform:translateX(-6px)} to{opacity:1;transform:translateX(0)} }
@keyframes sa__slideToggle { from{opacity:0;transform:translateY(-4px)} to{opacity:1;transform:translateY(0)} }

/* ── page ── */
.sa__wrap {
  font-family:'DM Sans',sans-serif;
  padding:22px 22px 40px;
  background:var(--sa-bg);
  min-height:100vh;
  animation:sa__fadeUp 0.4s ease both;
}

/* ── header ── */
.sa__hd { display:flex; align-items:flex-start; justify-content:space-between; gap:16px; margin-bottom:22px; flex-wrap:wrap; }
.sa__hd-left { display:flex; align-items:center; gap:14px; }
.sa__hd-icon {
  width:52px; height:52px; border-radius:16px; flex-shrink:0;
  background:linear-gradient(135deg,var(--sa-teal),var(--sa-teal-lite));
  display:flex; align-items:center; justify-content:center;
  font-size:22px; color:#fff; box-shadow:0 6px 20px rgba(10,85,86,0.30);
}
.sa__hd-title { font-family:'Sora',sans-serif; font-size:21px; font-weight:800; color:var(--sa-text); letter-spacing:-0.03em; margin:0 0 3px; }
.sa__hd-sub   { font-size:12.5px; color:var(--sa-muted); margin:0; }

/* ── main card ── */
.sa__card {
  background:var(--sa-white); border-radius:20px; border:1px solid var(--sa-border);
  box-shadow:0 4px 20px rgba(10,85,86,0.07); overflow:hidden; position:relative;
}
.sa__card::before {
  content:''; position:absolute; top:0; left:0; right:0; height:3px;
  background:linear-gradient(90deg,var(--sa-teal),var(--sa-teal-lite),var(--sa-gold));
}

/* ── toolbar ── */
.sa__toolbar {
  display:flex; align-items:center; justify-content:space-between;
  gap:12px; padding:16px 20px; border-bottom:1px solid rgba(10,85,86,0.07);
  flex-wrap:wrap; background:rgba(10,85,86,0.02);
}
.sa__toolbar-left  { display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
.sa__toolbar-right { display:flex; align-items:center; gap:8px; }
.sa__select {
  padding:8px 28px 8px 12px; font-size:12.5px; color:var(--sa-text); background:#f3fafa;
  border:1.5px solid rgba(10,85,86,0.14); border-radius:10px; outline:none; cursor:pointer; appearance:none;
  font-family:'DM Sans',sans-serif;
  background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='11' height='11' viewBox='0 0 24 24' fill='none' stroke='%235a8080' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat:no-repeat; background-position:right 9px center; transition:all 0.16s;
}
.sa__select:focus { border-color:var(--sa-teal); box-shadow:0 0 0 3px rgba(10,85,86,0.09); background:#fff; }
.sa__search {
  display:flex; align-items:center; gap:8px; background:#f3fafa;
  border:1.5px solid rgba(10,85,86,0.14); border-radius:10px; padding:8px 13px;
  transition:all 0.18s; width:240px;
}
.sa__search:focus-within { border-color:var(--sa-teal); box-shadow:0 0 0 3px rgba(10,85,86,0.09); background:#fff; }
.sa__search-icon  { color:var(--sa-muted); font-size:14px; flex-shrink:0; }
.sa__search-input { border:none; outline:none; background:transparent; font-size:13px; font-family:'DM Sans',sans-serif; color:var(--sa-text); flex:1; }
.sa__search-input::placeholder { color:#9ababa; }
.sa__count { font-size:12px; color:var(--sa-muted); font-family:'Sora',sans-serif; font-weight:600; white-space:nowrap; }
.sa__perpage { display:flex; align-items:center; gap:6px; }
.sa__perpage-lbl { font-size:12px; color:var(--sa-muted); font-family:'Sora',sans-serif; font-weight:600; }

/* ── table ── */
.sa__table-wrap { overflow-x:auto; }
.sa__table { width:100%; border-collapse:collapse; }
.sa__table thead th {
  font-family:'Sora',sans-serif; font-size:10.5px; font-weight:700;
  letter-spacing:0.09em; text-transform:uppercase; color:var(--sa-teal);
  background:rgba(10,85,86,0.035); padding:13px 16px;
  white-space:nowrap; border-bottom:1px solid var(--sa-border);
  cursor:pointer; user-select:none; transition:color 0.14s;
}
.sa__table thead th:hover { color:var(--sa-teal-lite); }
.sa__table thead th:first-child { padding-left:22px; }
.sa__table thead th:last-child  { padding-right:22px; text-align:center; }
.sa__sort-icon { display:inline-flex; vertical-align:middle; margin-left:4px; opacity:.3; }
.sa__sort-icon--on { opacity:1; color:var(--sa-teal-lite); }
.sa__table tbody tr { border-bottom:1px solid rgba(10,85,86,0.05); transition:background 0.13s; animation:sa__rowIn 0.22s ease both; }
.sa__table tbody tr:last-child { border-bottom:none; }
.sa__table tbody tr:hover { background:rgba(10,85,86,0.025); }
.sa__table tbody td { padding:13px 16px; font-size:13px; color:var(--sa-text); vertical-align:middle; }
.sa__table tbody td:first-child { padding-left:22px; }
.sa__table tbody td:last-child  { padding-right:22px; text-align:center; }

/* id chip */
.sa__id-chip {
  display:inline-flex; align-items:center; justify-content:center;
  font-family:'Sora',sans-serif; font-size:11px; font-weight:800;
  color:var(--sa-teal); background:rgba(10,85,86,0.09);
  border:1px solid rgba(10,85,86,0.18); border-radius:8px;
  padding:3px 10px; white-space:nowrap;
}

/* product cell */
.sa__prod-cell { display:flex; align-items:center; gap:12px; }
.sa__prod-thumb {
  width:42px; height:42px; border-radius:12px; object-fit:cover; flex-shrink:0;
  border:1.5px solid var(--sa-border); background:#e8f4f4;
}
.sa__prod-thumb--placeholder {
  width:42px; height:42px; border-radius:12px; flex-shrink:0;
  background:rgba(10,85,86,0.07); border:1.5px solid var(--sa-border);
  display:flex; align-items:center; justify-content:center;
  font-size:16px; color:var(--sa-muted);
}
.sa__prod-name { font-size:13px; font-weight:700; color:var(--sa-text); margin-bottom:2px; }
.sa__prod-uuid { font-size:10px; color:#9ababa; font-family:'Sora',sans-serif; letter-spacing:0.03em; }

/* type badge */
.sa__type-badge {
  display:inline-flex; align-items:center; gap:4px; padding:3px 10px;
  border-radius:50px; font-size:11px; font-family:'Sora',sans-serif; font-weight:700;
}
.sa__type-badge--product { background:rgba(10,85,86,0.09); color:var(--sa-teal); border:1px solid rgba(10,85,86,0.18); }
.sa__type-badge--service  { background:rgba(59,130,246,0.09); color:#2563eb; border:1px solid rgba(59,130,246,0.20); }

/* rate */
.sa__rate { font-family:'Sora',sans-serif; font-size:13.5px; font-weight:800; color:var(--sa-teal); text-align:right; }

/* stock display */
.sa__stock      { font-family:'Sora',sans-serif; font-size:13px; font-weight:700; text-align:center; display:inline-flex; align-items:center; gap:4px; }
.sa__stock--ok  { color:var(--sa-success); }
.sa__stock--low { color:var(--sa-danger); }
.sa__stock--na  { color:#b0cece; }

/* status badge */
.sa__status {
  display:inline-flex; align-items:center; gap:5px; padding:4px 10px;
  border-radius:50px; font-size:11px; font-family:'Sora',sans-serif; font-weight:700;
}
.sa__status--ok      { background:rgba(16,185,129,0.10); color:#065f46; border:1px solid rgba(16,185,129,0.25); }
.sa__status--low     { background:rgba(245,158,11,0.12); color:#92400e; border:1px solid rgba(245,158,11,0.30); }
.sa__status--out     { background:rgba(239,68,68,0.10); color:#991b1b; border:1px solid rgba(239,68,68,0.25); }
.sa__status--service { background:rgba(59,130,246,0.09); color:#1d4ed8; border:1px solid rgba(59,130,246,0.20); }

/* action buttons */
.sa__adjust-btn {
  display:inline-flex; align-items:center; gap:6px; padding:8px 15px;
  border-radius:10px; border:none; cursor:pointer;
  font-size:12.5px; font-family:'Sora',sans-serif; font-weight:700; color:#fff;
  background:linear-gradient(135deg,var(--sa-teal),var(--sa-teal-lite));
  box-shadow:0 3px 10px rgba(10,85,86,0.22); transition:all 0.15s;
}
.sa__adjust-btn:hover { transform:translateY(-1px); box-shadow:0 6px 16px rgba(10,85,86,0.30); }
.sa__locked { display:inline-flex; align-items:center; gap:5px; font-size:12px; font-family:'Sora',sans-serif; font-weight:600; color:#b0cece; }

/* ── skeleton ── */
.sa__skel {
  border-radius:8px;
  background:linear-gradient(90deg,#e8f4f4 0%,#d0eded 50%,#e8f4f4 100%);
  background-size:600px 100%; animation:sa__shimmer 1.4s linear infinite;
}
.sa__skel-row td { padding:14px 16px; }
.sa__skel-row td:first-child { padding-left:22px; }

/* ── empty ── */
.sa__empty { padding:60px 20px; text-align:center; }
.sa__empty-icon { width:62px; height:62px; border-radius:18px; margin:0 auto 14px; background:rgba(10,85,86,0.07); display:flex; align-items:center; justify-content:center; font-size:26px; }
.sa__empty-title { font-family:'Sora',sans-serif; font-size:16px; font-weight:700; color:var(--sa-text); margin-bottom:5px; }
.sa__empty-sub   { font-size:13px; color:var(--sa-muted); }

/* ── footer / pagination ── */
.sa__footer {
  display:flex; align-items:center; justify-content:space-between; gap:12px;
  padding:14px 22px; border-top:1px solid rgba(10,85,86,0.07);
  background:rgba(10,85,86,0.02); flex-wrap:wrap;
}
.sa__footer-info { font-size:12px; color:var(--sa-muted); font-family:'Sora',sans-serif; font-weight:600; }
.sa__pagination  { display:flex; align-items:center; gap:4px; flex-wrap:wrap; }
.sa__pg-btn {
  height:32px; padding:0 10px; min-width:32px; border-radius:9px;
  border:1.5px solid var(--sa-border); background:var(--sa-white); cursor:pointer;
  display:inline-flex; align-items:center; gap:4px;
  color:var(--sa-muted); font-size:12px; font-family:'Sora',sans-serif; font-weight:700;
  transition:all 0.14s;
}
.sa__pg-btn:hover:not(:disabled) { border-color:var(--sa-teal); color:var(--sa-teal); background:rgba(10,85,86,0.05); }
.sa__pg-btn:disabled { opacity:.35; cursor:not-allowed; }
.sa__pg-num {
  min-width:32px; height:32px; border-radius:9px; border:1.5px solid var(--sa-border);
  background:var(--sa-white); cursor:pointer; display:inline-flex; align-items:center; justify-content:center;
  font-size:12.5px; font-family:'Sora',sans-serif; font-weight:700; color:var(--sa-muted);
  transition:all 0.14s; padding:0 8px;
}
.sa__pg-num:hover   { border-color:var(--sa-teal); color:var(--sa-teal); }
.sa__pg-num--active { background:var(--sa-teal); color:#fff; border-color:var(--sa-teal); box-shadow:0 3px 8px rgba(10,85,86,0.25); }

/* ═══════════════════════════════════════
   ADJUSTMENT MODAL
═══════════════════════════════════════ */
.sa__overlay {
  position:fixed; inset:0; z-index:1050;
  background:rgba(10,30,30,0.60); backdrop-filter:blur(7px);
  display:flex; align-items:center; justify-content:center; padding:20px;
}
.sa__modal {
  background:var(--sa-white); border-radius:24px; width:100%; max-width:480px;
  box-shadow:0 30px 80px rgba(10,85,86,0.28); overflow:hidden; position:relative;
  animation:sa__modalIn 0.28s cubic-bezier(0.34,1.3,0.64,1) both;
}
.sa__modal::before {
  content:''; position:absolute; top:0; left:0; right:0; height:3px;
  background:linear-gradient(90deg,var(--sa-teal),var(--sa-teal-lite),var(--sa-gold));
}

/* modal header */
.sa__modal-hd {
  display:flex; align-items:center; justify-content:space-between;
  padding:18px 22px 14px; border-bottom:1px solid var(--sa-border);
}
.sa__modal-hd-left { display:flex; align-items:center; gap:12px; }
.sa__modal-hd-icon {
  width:44px; height:44px; border-radius:13px; flex-shrink:0;
  background:linear-gradient(135deg,var(--sa-teal),var(--sa-teal-lite));
  display:flex; align-items:center; justify-content:center; font-size:19px; color:#fff;
  box-shadow:0 4px 12px rgba(10,85,86,0.26);
}
.sa__modal-title { font-family:'Sora',sans-serif; font-weight:800; font-size:14.5px; color:var(--sa-text); margin:0 0 2px; }
.sa__modal-uuid  { font-size:10.5px; color:#9ababa; font-family:'Sora',sans-serif; }
.sa__modal-close {
  width:32px; height:32px; border-radius:9px; border:none; cursor:pointer;
  background:rgba(10,85,86,0.07); color:var(--sa-muted);
  display:flex; align-items:center; justify-content:center; transition:all 0.14s;
}
.sa__modal-close:hover { background:rgba(239,68,68,0.10); color:var(--sa-danger); }

/* modal body */
.sa__modal-body { padding:20px 22px; display:flex; flex-direction:column; gap:18px; }

/* stock pill */
.sa__stock-pill {
  background:rgba(10,85,86,0.04); border:1.5px solid var(--sa-border); border-radius:14px;
  padding:14px 18px; display:flex; align-items:center; justify-content:space-between;
}
.sa__stock-pill-lbl { font-size:11px; font-family:'Sora',sans-serif; font-weight:700; letter-spacing:0.07em; text-transform:uppercase; color:var(--sa-muted); margin-bottom:4px; }
.sa__stock-pill-val { font-family:'Sora',sans-serif; font-size:24px; font-weight:800; color:var(--sa-text); }
.sa__stock-pill-unit { font-size:12px; font-weight:600; color:var(--sa-muted); margin-left:5px; }
.sa__stock-pill-icon { width:44px; height:44px; border-radius:13px; background:linear-gradient(135deg,var(--sa-teal),var(--sa-teal-lite)); display:flex; align-items:center; justify-content:center; font-size:19px; color:#fff; }

/* toggle */
.sa__toggle-grid { display:grid; grid-template-columns:1fr 1fr; gap:10px; }
.sa__toggle-opt {
  padding:14px 16px; border-radius:14px; cursor:pointer;
  display:flex; flex-direction:column; align-items:center; gap:7px;
  font-family:'Sora',sans-serif; font-size:13px; font-weight:700; color:var(--sa-muted);
  border:2px solid var(--sa-border); background:transparent; transition:all 0.18s;
}
.sa__toggle-opt-icon { font-size:20px; }
.sa__toggle-opt--add:hover    { border-color:var(--sa-success); color:var(--sa-success); background:rgba(16,185,129,0.04); }
.sa__toggle-opt--sub:hover    { border-color:var(--sa-danger); color:var(--sa-danger); background:rgba(239,68,68,0.04); }
.sa__toggle-opt--add.sa__toggle-active { border-color:var(--sa-success); color:var(--sa-success); background:rgba(16,185,129,0.08); }
.sa__toggle-opt--sub.sa__toggle-active { border-color:var(--sa-danger); color:var(--sa-danger); background:rgba(239,68,68,0.08); }

/* form fields */
.sa__fields { display:grid; grid-template-columns:2fr 3fr; gap:12px; }
.sa__field-lbl {
  display:block; font-size:11px; font-family:'Sora',sans-serif; font-weight:700;
  letter-spacing:0.06em; text-transform:uppercase; color:var(--sa-muted); margin-bottom:7px;
}
.sa__input {
  width:100%; padding:10px 14px; font-size:13.5px; color:var(--sa-text);
  background:#f3fafa; border:1.5px solid rgba(10,85,86,0.14); border-radius:11px;
  outline:none; font-family:'DM Sans',sans-serif; font-weight:600; transition:all 0.16s;
}
.sa__input:focus { border-color:var(--sa-teal); box-shadow:0 0 0 3px rgba(10,85,86,0.09); background:#fff; }
.sa__input--qty { font-family:'Sora',sans-serif; font-weight:800; font-size:15px; text-align:center; }

/* modal footer */
.sa__modal-ft {
  display:flex; align-items:center; gap:10px; padding:16px 22px;
  border-top:1px solid var(--sa-border); background:rgba(10,85,86,0.02);
}
.sa__modal-cancel {
  flex:1; padding:11px; border-radius:12px; cursor:pointer;
  font-size:13px; font-family:'Sora',sans-serif; font-weight:700;
  color:var(--sa-muted); background:rgba(10,85,86,0.06);
  border:1.5px solid rgba(10,85,86,0.14); transition:all 0.15s;
}
.sa__modal-cancel:hover { background:rgba(10,85,86,0.12); color:var(--sa-teal); }
.sa__modal-submit {
  flex:2; padding:11px; border-radius:12px; border:none; cursor:pointer;
  font-size:13px; font-family:'Sora',sans-serif; font-weight:700; color:#fff;
  transition:all 0.15s; display:flex; align-items:center; justify-content:center; gap:7px;
}
.sa__modal-submit--add { background:linear-gradient(135deg,#059669,var(--sa-success)); box-shadow:0 4px 14px rgba(16,185,129,0.28); }
.sa__modal-submit--sub { background:linear-gradient(135deg,#b91c1c,var(--sa-danger)); box-shadow:0 4px 14px rgba(239,68,68,0.26); }
.sa__modal-submit--add:hover { box-shadow:0 6px 20px rgba(16,185,129,0.36); transform:translateY(-1px); }
.sa__modal-submit--sub:hover { box-shadow:0 6px 20px rgba(239,68,68,0.34); transform:translateY(-1px); }
.sa__modal-submit:disabled { opacity:.5; cursor:not-allowed; transform:none; box-shadow:none; }

@media(max-width:600px){
  .sa__search{ width:100%; }
  .sa__toolbar{ flex-direction:column; align-items:stretch; }
  .sa__fields { grid-template-columns:1fr; }
  .sa__footer { flex-direction:column; align-items:flex-start; }
}
`;

/* ─── utils (unchanged) ─── */
const formatCurrency = (amount) =>
  Number(amount || 0).toLocaleString("en-IN", { style:"currency", currency:"INR", minimumFractionDigits:2, maximumFractionDigits:2 });

const getStatusInfo = (stock, type) => {
  const n = parseFloat(stock);
  if (type === "Service") return { label:"Service", cls:"sa__status--service", dot:"#3b82f6" };
  if (isNaN(n) || stock === null) return { label:"No Data", cls:"sa__status--out", dot:"#94a3b8" };
  if (n <= 0)  return { label:"Out of Stock", cls:"sa__status--out",  dot:"var(--sa-danger)" };
  if (n <= 10) return { label:"Low Stock",    cls:"sa__status--low",  dot:"var(--sa-warning)" };
  return              { label:"In Stock",     cls:"sa__status--ok",   dot:"var(--sa-success)" };
};

/* ─── skeleton rows ─── */
const SkeletonRows = ({ count = 8 }) => Array.from({ length: count }).map((_, i) => (
  <tr className="sa__skel-row" key={i}>
    <td><div className="sa__skel" style={{ width:44, height:24, borderRadius:8 }} /></td>
    <td>
      <div style={{ display:"flex", alignItems:"center", gap:12 }}>
        <div className="sa__skel" style={{ width:42, height:42, borderRadius:12, flexShrink:0 }} />
        <div>
          <div className="sa__skel" style={{ width:120, height:12, marginBottom:6 }} />
          <div className="sa__skel" style={{ width:90, height:9 }} />
        </div>
      </div>
    </td>
    {[70,80,60,80,70].map((w,j)=><td key={j}><div className="sa__skel" style={{ width:w, height:22, borderRadius:50, marginLeft:j===2?'auto':j===3?'auto':0, marginRight:j===2?'auto':0 }} /></td>)}
  </tr>
));

/* ─── Pagination (unchanged logic) ─── */
const Pagination = ({ totalItems, itemsPerPage, currentPage, onPageChange }) => {
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  if (totalItems === 0) return null;
  let start = Math.max(1, currentPage - 2);
  let end   = Math.min(totalPages, currentPage + 2);
  if (currentPage <= 3)         { start = 1; end = Math.min(5, totalPages); }
  else if (currentPage + 2 >= totalPages) { end = totalPages; start = Math.max(1, totalPages - 4); }
  const pages = [];
  for (let i = start; i <= end; i++) pages.push(i);
  return (
    <div className="sa__pagination">
      <button className="sa__pg-btn" disabled={currentPage===1} onClick={()=>onPageChange(1)}>First</button>
      <button className="sa__pg-btn" disabled={currentPage===1} onClick={()=>onPageChange(currentPage-1)}><FiChevronLeft size={12}/> Prev</button>
      {pages.map(p=>(
        <button key={p} className={`sa__pg-num ${currentPage===p?"sa__pg-num--active":""}`} onClick={()=>onPageChange(p)}>{p}</button>
      ))}
      <button className="sa__pg-btn" disabled={currentPage===totalPages} onClick={()=>onPageChange(currentPage+1)}>Next <FiChevronRight size={12}/></button>
      <button className="sa__pg-btn" disabled={currentPage===totalPages} onClick={()=>onPageChange(totalPages)}>Last</button>
    </div>
  );
};

/* ─── StockAdjustmentModal (unchanged logic, new UI) ─── */
const StockAdjustmentModal = ({ show, handleClose, product, handleConfirmAdjustment }) => {
  const [adjustmentQuantity, setAdjustmentQuantity] = useState(1);
  const [adjustmentReason, setAdjustmentReason]     = useState("");
  const [adjustmentType, setAdjustmentType]         = useState("add");

  useEffect(() => {
    if (show) { setAdjustmentQuantity(1); setAdjustmentReason(""); setAdjustmentType("add"); }
  }, [show, product]);

  if (!show || !product) return null;

  const currentStock = parseFloat(product.current_stock || 0);

  const handleConfirm = () => {
    if (adjustmentQuantity <= 0 || !adjustmentReason.trim()) return;
    if (adjustmentType === "subtract" && currentStock - adjustmentQuantity < 0) {
      alert("Insufficient stock to perform this reduction.");
      return;
    }
    handleConfirmAdjustment(product.product_uuid, adjustmentQuantity, adjustmentType, adjustmentReason);
    handleClose();
  };

  return (
    <div className="sa__overlay" onClick={e => { if(e.target===e.currentTarget) handleClose(); }}>
      <div className="sa__modal">
        {/* header */}
        <div className="sa__modal-hd">
          <div className="sa__modal-hd-left">
            <div className="sa__modal-hd-icon"><FiEdit3 /></div>
            <div>
              <div className="sa__modal-title">{product.product_data?.name}</div>
              <div className="sa__modal-uuid">{product.product_uuid?.slice(0,13)}…</div>
            </div>
          </div>
          <button className="sa__modal-close" onClick={handleClose}><FiX size={14} /></button>
        </div>

        {/* body */}
        <div className="sa__modal-body">
          {/* stock pill */}
          <div className="sa__stock-pill">
            <div>
              <div className="sa__stock-pill-lbl">Available Stock</div>
              <div>
                <span className="sa__stock-pill-val">{currentStock}</span>
                <span className="sa__stock-pill-unit">{product.product_data?.unit || "Units"}</span>
              </div>
            </div>
            <div className="sa__stock-pill-icon"><FiPackage /></div>
          </div>

          {/* type toggle */}
          <div className="sa__toggle-grid">
            {[
              { t:"add", label:"Restock", icon:<FiPlusCircle size={20} />, cls:"sa__toggle-opt--add" },
              { t:"subtract", label:"Reduce", icon:<FiMinus size={20} />, cls:"sa__toggle-opt--sub" },
            ].map(({ t, label, icon, cls }) => (
              <div key={t}
                className={`sa__toggle-opt ${cls} ${adjustmentType===t ? "sa__toggle-active" : ""}`}
                onClick={() => setAdjustmentType(t)}>
                <span className="sa__toggle-opt-icon">{icon}</span>
                <span>{label}</span>
              </div>
            ))}
          </div>

          {/* qty + reason */}
          <div className="sa__fields">
            <div>
              <label className="sa__field-lbl">Quantity</label>
              <input type="number" className="sa__input sa__input--qty"
                value={adjustmentQuantity}
                onChange={e => setAdjustmentQuantity(Math.max(1, Number(e.target.value)))} />
            </div>
            <div>
              <label className="sa__field-lbl">Reason</label>
              <select className="sa__input" value={adjustmentReason} onChange={e => setAdjustmentReason(e.target.value)}>
                <option value="">Select a reason…</option>
                <optgroup label="Stock Additions">
                  <option value="Goods Received">Goods Received (New Purchase)</option>
                  <option value="Customer Return">Customer Return (Restockable)</option>
                  <option value="Production Surplus">Production Surplus</option>
                  <option value="Found in Warehouse">Found in Warehouse (Uncounted)</option>
                </optgroup>
                <optgroup label="Stock Reductions">
                  <option value="Damage/Breakage">Damage/Breakage</option>
                  <option value="Expired Stock">Expired Stock</option>
                  <option value="Internal Use">Internal Use (Office/Testing)</option>
                  <option value="Theft/Shrinkage">Theft/Shrinkage</option>
                  <option value="Sample/Gift">Promotional Sample/Gift</option>
                  <option value="Return to Vendor">Return to Vendor (Defective)</option>
                </optgroup>
                <optgroup label="Audit & Corrections">
                  <option value="Inventory Audit">Inventory Audit Correction</option>
                  <option value="Data Entry Error">Data Entry Error Correction</option>
                  <option value="Reclassification">Product Reclassification</option>
                </optgroup>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>
        </div>

        {/* footer */}
        <div className="sa__modal-ft">
          <button className="sa__modal-cancel" onClick={handleClose}>Discard</button>
          <button
            className={`sa__modal-submit ${adjustmentType==="add" ? "sa__modal-submit--add" : "sa__modal-submit--sub"}`}
            onClick={handleConfirm}
            disabled={!adjustmentReason}>
            {adjustmentType === "add" ? <FiPlusCircle size={14} /> : <FiMinus size={14} />}
            Update Inventory
          </button>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════ */
function StockAdjustment() {
  const [products, setProducts]           = useState([]);
  const [loading, setLoading]             = useState(true);
  const token = localStorage.getItem("auth_token");

  const [searchTerm, setSearchTerm]       = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [sortBy, setSortBy]               = useState({ key:"id", direction:"desc" });

  const [showModal, setShowModal]         = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const [currentPage, setCurrentPage]     = useState(1);
  const [itemsPerPage, setItemsPerPage]   = useState(10);

  /* ── fetch (unchanged) ── */
  const fetchInventory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${baseurl}getAllProductStock`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) setProducts(res.data.data);
    } catch (error) { console.error("FETCH ERROR:", error); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchInventory(); }, [fetchInventory]);

  /* ── processedProducts (unchanged) ── */
  const processedProducts = useMemo(() => {
    let list = [...products];
    if (searchTerm.trim() !== "") {
      const s = searchTerm.trim().toLowerCase();
      list = list.filter(i =>
        i.id?.toString().includes(s) ||
        i.product_data?.name?.toLowerCase().includes(s) ||
        i.type?.toLowerCase().includes(s)
      );
    }
    if (categoryFilter !== "All") list = list.filter(i => i.type === categoryFilter);
    list.sort((a, b) => {
      let aVal, bVal;
      if (sortBy.key === "name")  { aVal = a.product_data?.name || ""; bVal = b.product_data?.name || ""; }
      else if (sortBy.key === "rate")  { aVal = parseFloat(a.product_data?.rate || 0); bVal = parseFloat(b.product_data?.rate || 0); }
      else if (sortBy.key === "stock") { aVal = parseFloat(a.current_stock || 0); bVal = parseFloat(b.current_stock || 0); }
      else { aVal = a[sortBy.key]; bVal = b[sortBy.key]; }
      let cmp = (typeof aVal === "number") ? aVal - bVal : String(aVal).localeCompare(String(bVal));
      return sortBy.direction === "asc" ? cmp : -cmp;
    });
    return list;
  }, [products, searchTerm, categoryFilter, sortBy]);

  useEffect(() => { setCurrentPage(1); }, [searchTerm, categoryFilter, sortBy, itemsPerPage]);

  const totalItems  = processedProducts.length;
  const startIndex  = (currentPage - 1) * itemsPerPage;
  const endIndex    = Math.min(startIndex + itemsPerPage, totalItems);
  const paginated   = processedProducts.slice(startIndex, endIndex);

  const handleSort = (key) => {
    setSortBy(prev => ({ key, direction: prev.key===key && prev.direction==="asc" ? "desc" : "asc" }));
  };

  const allCategories = useMemo(() => ["All", ...new Set(products.map(p => p.type))], [products]);

  const openAdjustmentModal  = useCallback((product) => { setSelectedProduct(product); setShowModal(true); }, []);
  const closeAdjustmentModal = useCallback(() => { setShowModal(false); setSelectedProduct(null); }, []);

  /* ── Toast (unchanged) ── */
  const Toast = Swal.mixin({
    toast: true, position:"top-end", showConfirmButton:false,
    timer:3000, timerProgressBar:true,
    didOpen: (toast) => {
      toast.addEventListener("mouseenter", Swal.stopTimer);
      toast.addEventListener("mouseleave", Swal.resumeTimer);
    },
  });

  /* ── handleConfirmAdjustment (unchanged) ── */
  const handleConfirmAdjustment = async (uuid, qty, type, reason) => {
    const payload = { product_uuid:uuid, adjustment_qty:qty, type, reason };
    console.log("SENT DATA TO BACKEND:", payload);
    Toast.fire({ icon:"info", title:"Updating inventory…", timer:1000, timerProgressBar:false });
    try {
      const res = await axios.post(`${baseurl}updateStockByUuid`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        fetchInventory();
        Toast.fire({ icon:"success", title:"Stock Updated Successfully", text:`${type==="add"?"+":"-"}${qty} units recorded.` });
      } else {
        Toast.fire({ icon:"error", title:"Adjustment Failed", text: res.data.message || "Please check provided data." });
      }
    } catch (error) {
      console.error("ADJUSTMENT ERROR:", error);
      Swal.fire({ icon:"error", title:"Server Connection Error", text: error.response?.data?.message || "Internal Server Error. Please try again later.", confirmButtonColor:"#0a5556" });
    }
  };

  /* ── renderStockDisplay (unchanged) ── */
  const renderStockDisplay = (product) => {
    if (product.type === "Service") return <span className="sa__stock sa__stock--na">N/A</span>;
    const stock = parseFloat(product.current_stock);
    if (isNaN(stock) || product.current_stock === null)
      return <span className="sa__stock sa__stock--na">Not Tracked</span>;
    return (
      <span className={`sa__stock ${stock <= 10 ? "sa__stock--low" : "sa__stock--ok"}`}>
        {stock <= 10 && <FiAlertTriangle size={12} />}
        {stock} <span style={{ fontSize:10.5, fontWeight:500, color:"var(--sa-muted)", marginLeft:3 }}>{product.product_data?.unit}</span>
      </span>
    );
  };

  const SortIcon = ({ columnKey }) => {
    if (sortBy.key !== columnKey) return <span className="sa__sort-icon"><FiChevronsUp size={11}/></span>;
    return sortBy.direction === "asc"
      ? <span className="sa__sort-icon sa__sort-icon--on"><FiChevronsUp size={11}/></span>
      : <span className="sa__sort-icon sa__sort-icon--on"><FiChevronsDown size={11}/></span>;
  };

  /* ═══ RENDER ═══ */
  return (
    <>
      <style>{CSS}</style>
      <Topbar /><Sidebar />

      <main className="main-wrapper">
        <div className="sa__wrap">

          {/* Header */}
          <div className="sa__hd">
            <div className="sa__hd-left">
              <div className="sa__hd-icon"><FiEdit3 /></div>
              <div>
                <div className="sa__hd-title">Stock Adjustment</div>
                <div className="sa__hd-sub">Directly manage stock levels and maintain audit trails</div>
              </div>
            </div>
          </div>

          {/* Main Card */}
          <div className="sa__card">

            {/* Toolbar */}
            <div className="sa__toolbar">
              <div className="sa__toolbar-left">
                <select className="sa__select" value={categoryFilter}
                  onChange={e => setCategoryFilter(e.target.value)}>
                  {allCategories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <div className="sa__search">
                  <FiSearch className="sa__search-icon" />
                  <input className="sa__search-input" placeholder="Search name, ID, type…"
                    value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                  {searchTerm && (
                    <FiX size={13} style={{ color:"var(--sa-muted)", cursor:"pointer" }}
                      onClick={() => setSearchTerm("")} />
                  )}
                </div>
              </div>
              <div className="sa__toolbar-right">
                <span className="sa__count">{totalItems} items</span>
                <div className="sa__perpage">
                  <span className="sa__perpage-lbl">Show</span>
                  <select className="sa__select" style={{ width:"auto" }} value={itemsPerPage}
                    onChange={e => setItemsPerPage(Number(e.target.value))}>
                    {[10,25,50].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="sa__table-wrap">
              <table className="sa__table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort("id")}>ID <SortIcon columnKey="id" /></th>
                    <th onClick={() => handleSort("name")}>Item Details <SortIcon columnKey="name" /></th>
                    <th onClick={() => handleSort("type")}>Type <SortIcon columnKey="type" /></th>
                    <th style={{ textAlign:"right" }} onClick={() => handleSort("rate")}>Rate <SortIcon columnKey="rate" /></th>
                    <th style={{ textAlign:"center" }} onClick={() => handleSort("stock")}>Current Stock <SortIcon columnKey="stock" /></th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <SkeletonRows count={itemsPerPage} />
                  ) : paginated.length === 0 ? (
                    <tr><td colSpan="7">
                      <div className="sa__empty">
                        <div className="sa__empty-icon">🔍</div>
                        <div className="sa__empty-title">No records found</div>
                        <div className="sa__empty-sub">Try adjusting your search or filter.</div>
                      </div>
                    </td></tr>
                  ) : paginated.map(prod => {
                    const status = getStatusInfo(prod.current_stock, prod.type);
                    return (
                      <tr key={prod.id}>
                        <td><span className="sa__id-chip">#{prod.id}</span></td>
                        <td>
                          <div className="sa__prod-cell">
                            {prod.product_data?.image_path ? (
                              <img className="sa__prod-thumb" src={`${imgurl}${prod.product_data.image_path}`} alt={prod.product_data?.name} />
                            ) : (
                              <div className="sa__prod-thumb--placeholder"><FiBox /></div>
                            )}
                            <div>
                              <div className="sa__prod-name">{prod.product_data?.name}</div>
                              <div className="sa__prod-uuid">{prod.product_uuid}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className={`sa__type-badge ${prod.type==="Product" ? "sa__type-badge--product" : "sa__type-badge--service"}`}>
                            <FiTag size={10} /> {prod.type}
                          </span>
                        </td>
                        <td><div className="sa__rate">{formatCurrency(prod.product_data?.rate)}</div></td>
                        <td style={{ textAlign:"center" }}>{renderStockDisplay(prod)}</td>
                        <td>
                          <span className={`sa__status ${status.cls}`}>
                            <span style={{ width:6, height:6, borderRadius:"50%", background:status.dot, display:"inline-block", flexShrink:0 }} />
                            {status.label}
                          </span>
                        </td>
                        <td>
                          {prod.type === "Product" && prod.product_data?.inventory_tracked !== false ? (
                            <button className="sa__adjust-btn" onClick={() => openAdjustmentModal(prod)}>
                              <FiEdit3 size={12} /> Adjust
                            </button>
                          ) : (
                            <span className="sa__locked"><LockIcon size={11} /> Locked</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Footer */}
            {!loading && (
              <div className="sa__footer">
                <div className="sa__footer-info">
                  Showing <strong>{startIndex + 1}</strong> to <strong>{endIndex}</strong> of <strong>{totalItems}</strong> records
                </div>
                <Pagination totalItems={totalItems} itemsPerPage={itemsPerPage} currentPage={currentPage} onPageChange={setCurrentPage} />
              </div>
            )}
          </div>

        </div>
      </main>

      <StockAdjustmentModal
        show={showModal}
        handleClose={closeAdjustmentModal}
        product={selectedProduct}
        handleConfirmAdjustment={handleConfirmAdjustment}
      />
    </>
  );
}

export default StockAdjustment;
